/**
 * Cheat Sheet content for all admin & team dashboards.
 * Each key matches a page identifier. Each entry has a title and sections array.
 */

const CHEAT_SHEETS = {
  // ── Master Admin Pages ──
  analytics: {
    title: 'Analytics Dashboard',
    sections: [
      {
        heading: 'Overview Metrics',
        bullets: [
          'Total registered users, active users (7d/30d), and new sign-ups are shown as live KPI cards.',
          'Click any metric card to drill down into the filtered user list.',
          'Date range selector (top-right) filters all charts and numbers on the page.',
        ],
      },
      {
        heading: 'Growth Charts',
        bullets: [
          'User growth chart shows cumulative registrations over time.',
          'Daily active users (DAU) trend shows engagement patterns — look for weekend dips.',
          'Retention cohort table tracks how many users return after N days.',
        ],
      },
      {
        heading: 'Engagement Breakdown',
        bullets: [
          'Session duration and messages-per-session indicate conversation depth.',
          'Top pathways chart shows which AvA focus areas are most popular.',
          'Geographic distribution maps where your users are located.',
        ],
      },
    ],
  },

  users: {
    title: 'User Management',
    sections: [
      {
        heading: 'User Table',
        bullets: [
          'Search by name, email, or user ID. Filters: role, status, 2FA, date range.',
          'Click a row to open the full user profile with conversation history.',
          'Bulk actions: select multiple users to send notifications or change roles.',
        ],
      },
      {
        heading: 'Roles & Permissions',
        bullets: [
          'Roles: admin, sub_admin, corporate_admin, school_admin, parent, teen, user.',
          'Sub-admins have domain-scoped access (e.g., "users" domain = Users + Safety pages only).',
          'Role changes are audit-logged and trigger a notification to the affected user.',
        ],
      },
      {
        heading: 'Account Actions',
        bullets: [
          'Suspend/Unsuspend: temporarily blocks login without deleting data.',
          'Force Password Reset: invalidates current password and sends reset email.',
          'Delete Account: permanent removal — requires confirmation and is audit-logged.',
        ],
      },
    ],
  },

  leads: {
    title: 'Lead Generation',
    sections: [
      {
        heading: 'Lead Sources',
        bullets: [
          'Leads are auto-imported from Reddit (via RapidAPI) based on keyword matching.',
          'Each lead shows source platform, relevance score, and conversation context.',
          'Status workflow: New → Contacted → Qualified → Converted → Archived.',
        ],
      },
      {
        heading: 'Lead Alerts',
        bullets: [
          'High-relevance leads (score 8+) trigger a notification in the admin panel.',
          'Alert thresholds are configurable in Settings → Lead Generation.',
          'Weekly digest email summarizes new leads and conversion rates.',
        ],
      },
    ],
  },

  revenue: {
    title: 'Revenue & Subscriptions',
    sections: [
      {
        heading: 'Revenue Overview',
        bullets: [
          'MRR (Monthly Recurring Revenue), ARR, and churn rate are shown as headline metrics.',
          'Revenue chart breaks down by plan tier (Free, Pro, Enterprise).',
          'Subscription table shows all active/cancelled plans with renewal dates.',
        ],
      },
      {
        heading: 'Payment Processing',
        bullets: [
          'Payments are LIVE — Stripe on web (merchant-of-record checkout) and Google Play Billing on Android.',
          'Failed payments trigger automatic retry (3 attempts over 7 days).',
          'Refund requests appear in the queue — approve or deny with one click.',
        ],
      },
    ],
  },

  safety: {
    title: 'Safety & Guardrails',
    sections: [
      {
        heading: 'Content Moderation',
        bullets: [
          'All AvA conversations pass through the guardrails classifier before reaching the user.',
          'Flagged messages are queued for human review with context and severity level.',
          'Override a flag to mark as safe — the classifier learns from overrides.',
        ],
      },
      {
        heading: 'Safety Metrics',
        bullets: [
          'Flag rate shows what percentage of messages trigger a safety check.',
          'False positive rate tracks unnecessary flags — aim for under 5%.',
          'Escalation queue shows messages requiring immediate human attention.',
        ],
      },
    ],
  },

  security: {
    title: 'Security Dashboard',
    sections: [
      {
        heading: 'Security Score & Compliance',
        bullets: [
          'Score (0-100) is a composite of 6 factors: 2FA adoption, key rotation, rate limiting, login health, active defense, device trust.',
          'SOC2/ISO checklist auto-evaluates 10 compliance items. Green = pass, yellow = warning, red = fail.',
          'Score history trend line appears after 2+ daily snapshots — shows security posture over time.',
        ],
      },
      {
        heading: 'Threat Intelligence & Anomalies',
        bullets: [
          'Hourly attack chart shows WHEN attacks happen — useful for spotting bot patterns.',
          'Attack velocity tracks daily failed login count over 7 days.',
          'Anomaly cards detect: off-hours activity (11PM-5AM), new IPs, and rapid-fire attacks.',
        ],
      },
      {
        heading: 'IP Defense System',
        bullets: [
          'Manual Block: enter any IP to immediately block from all API access (HTTP 403).',
          'Auto-Block: configure threshold (e.g., 10 failures in 5 min = auto-block for 24h).',
          'Whitelist: add trusted IPs (office, VPN) that are never auto-blocked.',
          'Bulk Block: one-click blocks ALL flagged IPs with 5+ failed attempts.',
        ],
      },
      {
        heading: 'Alerts & Reporting',
        bullets: [
          'SMS Alerts: configure phone numbers to receive real-time security alerts via Twilio.',
          'Weekly Digest: preview and send a branded HTML email with score, threats, and metrics.',
          'CSV Export: download full audit log as a spreadsheet for compliance audits.',
          'Timeline: view a chronological log of all security events with action icons.',
        ],
      },
      {
        heading: 'User & Account Security',
        bullets: [
          'User Health panel ranks users by risk score (no 2FA, idle accounts, old passwords).',
          'Account Lockout: lock/unlock user accounts directly from the dashboard.',
          'Recommendations engine suggests prioritized actions to improve your security score.',
        ],
      },
    ],
  },

  audit: {
    title: 'Audit Trail',
    sections: [
      {
        heading: 'Event Log',
        bullets: [
          'Every admin action is logged: user changes, config updates, IP blocks, key rotations.',
          'Filter by action type, user, IP address, or date range.',
          'Click any event to see full metadata including request details.',
        ],
      },
      {
        heading: 'Export & Compliance',
        bullets: [
          'Export filtered logs as CSV for external audit systems.',
          'Retention: audit logs are kept indefinitely — no auto-deletion.',
          'Required for SOC2 Type II and ISO 27001 certification evidence.',
        ],
      },
    ],
  },

  content: {
    title: 'Content Manager',
    sections: [
      {
        heading: 'AvA Image Library',
        bullets: [
          'Upload images individually or via drag-and-drop bulk upload.',
          'Each image has: category, folder, teen-allowed flag, and metadata.',
          'Categories: Positive, Assertive, Calm, Creative, Reflective, Fun.',
          'Portrait 3:4 aspect ratio matches the user dashboard display.',
        ],
      },
      {
        heading: 'Image Management',
        bullets: [
          'Edit metadata inline — click pencil icon on any image card.',
          'Delete images with confirmation — removal is permanent and audit-logged.',
          'Filter by category, folder, or teen-allowed status.',
        ],
      },
    ],
  },

  features: {
    title: 'Feature Flags',
    sections: [
      {
        heading: 'Flag Management',
        bullets: [
          'Feature flags control which features are visible to users in real-time.',
          'Toggle ON/OFF without deploying code — changes take effect immediately.',
          'Percentage rollout: gradually expose a feature to 10%, 50%, 100% of users.',
        ],
      },
      {
        heading: 'Flag Types',
        bullets: [
          'Boolean: simple ON/OFF toggle for a feature.',
          'Percentage: roll out to a specific % of users (A/B testing).',
          'User-targeted: enable for specific user IDs or roles only.',
        ],
      },
    ],
  },

  notifications: {
    title: 'Notifications',
    sections: [
      {
        heading: 'Push Notifications',
        bullets: [
          'Send targeted notifications to specific users, roles, or everyone.',
          'Schedule notifications for future delivery (e.g., Monday 9AM).',
          'Templates: pre-built notification templates for common scenarios.',
        ],
      },
      {
        heading: 'SMS Nudges',
        bullets: [
          'Weekly SMS nudges are tied to user focus areas and pathway progress.',
          'Max 1 SMS per user per week. Respects quiet hours (9PM-9AM).',
          'Template library organized by focus tag (new_friends, speak_up, etc.).',
        ],
      },
    ],
  },

  schools: {
    title: 'Schools Management',
    sections: [
      {
        heading: 'School Accounts',
        bullets: [
          'Schools register via the public registration page and get a 14-day free trial (up to 10 students).',
          'Each school gets a dedicated dashboard with aggregated student wellness data.',
          'School admins can manage students, view reports, and configure their AvA instance.',
        ],
      },
      {
        heading: 'Monitoring',
        bullets: [
          'Track student engagement, session counts, and pathway progress across schools.',
          'Monthly auto-generated reports are sent to school administrators and parents.',
          'Flag schools with low engagement for outreach and support.',
        ],
      },
    ],
  },

  corporates: {
    title: 'Corporate Partners',
    sections: [
      {
        heading: 'Corporate Accounts',
        bullets: [
          'Corporate partners register via a dedicated flow with company details.',
          'Each corporate gets a full dashboard: overview, staff, engagement, goals, reports.',
          'Access codes let corporates invite their employees to the platform.',
        ],
      },
      {
        heading: 'Enterprise Features',
        bullets: [
          'ROI calculator demonstrates value to HR/leadership teams.',
          'Benchmark comparison shows how the company compares to industry averages.',
          'Board presentation mode generates executive-ready slides from live data.',
        ],
      },
    ],
  },

  reports: {
    title: 'Reports',
    sections: [
      {
        heading: 'Report Generation',
        bullets: [
          'Generate on-demand reports: user growth, engagement, safety, revenue.',
          'Schedule weekly or monthly auto-reports delivered via email.',
          'Export reports as PDF, CSV, or direct to email.',
        ],
      },
    ],
  },

  team: {
    title: 'Team Management',
    sections: [
      {
        heading: 'Sub-Admin Accounts',
        bullets: [
          'Create sub-admin accounts with domain-scoped access (users, content, analytics, etc.).',
          'Each sub-admin sees only the sidebar sections relevant to their domain.',
          'Activity log shows what each team member accessed and changed.',
        ],
      },
    ],
  },

  vault: {
    title: 'Secure Vault',
    sections: [
      {
        heading: 'API Key Storage',
        bullets: [
          'Store third-party API keys encrypted at rest (Twilio, Postmark, RunPod, etc.).',
          'Keys are masked in the UI — click "Reveal" to see the full value.',
          'Rotation reminders appear when keys are older than 90 days.',
        ],
      },
    ],
  },

  settings: {
    title: 'Platform Settings',
    sections: [
      {
        heading: 'Configuration',
        bullets: [
          'Toggle platform-wide features: sign-ups, SMS nudges, email notifications.',
          'Set default user settings: language, timezone, notification preferences.',
          'Manage platform branding: logo, colors, email templates.',
        ],
      },
    ],
  },

  'developer-support': {
    title: 'Developer Support',
    sections: [
      {
        heading: 'Ticketing System',
        bullets: [
          'Sub-admins and team members can submit support tickets here.',
          'Tickets are categorized: bug, feature request, question, access issue.',
          'Master admin can assign, respond, and close tickets.',
        ],
      },
    ],
  },

  logs: {
    title: 'System Logs',
    sections: [
      {
        heading: 'Log Viewer',
        bullets: [
          'All API requests are logged with method, path, status code, and response time.',
          'Search by message text, filter by level (INFO/WARNING/ERROR/CRITICAL) and source.',
          'Time range selector: 1h, 6h, 24h, 3d, 7d, 30d — adjusts all charts and results.',
          'Paginated results (50 per page) — click page arrows to navigate.',
        ],
      },
      {
        heading: 'Monitoring & Analytics',
        bullets: [
          'Log Volume chart shows request count + error overlay over time.',
          'Level Distribution pie chart breaks down INFO vs WARNING vs ERROR.',
          'Top Errors section shows most frequent error messages with count.',
          'Sources breakdown shows which modules generate the most logs.',
        ],
      },
      {
        heading: 'Operations',
        bullets: [
          'Auto-Refresh (Live mode): polls every 10 seconds for new logs.',
          'Export: download filtered logs as CSV for external analysis.',
          'Logs auto-expire after 90 days (TTL index on MongoDB).',
          '10% of successful requests are sampled — all errors and slow requests (>2s) are always captured.',
        ],
      },
    ],
  },

  // ── Corporate Dashboard ──
  'corporate-dashboard': {
    title: 'Corporate Dashboard',
    sections: [
      {
        heading: 'Overview Tab',
        bullets: [
          'KPI cards show total staff, active users, avg engagement score, and top pathways.',
          'Engagement trend chart tracks daily active users over the selected period.',
          'Quick stats: session count, messages sent, avg session duration.',
        ],
      },
      {
        heading: 'Staff & Access',
        bullets: [
          'Staff tab lists all employees on the platform with status and activity.',
          'Access Codes tab manages invite codes for new employees to join.',
          'Locations tab breaks down engagement by office/branch location.',
        ],
      },
      {
        heading: 'Insights & Reporting',
        bullets: [
          'Health Map visualizes wellbeing scores across the organization.',
          'Pulse surveys let you check team sentiment with quick 1-5 ratings.',
          'Reports tab generates downloadable engagement and wellness reports.',
          'ROI tab calculates estimated cost savings from improved employee wellbeing.',
        ],
      },
    ],
  },

  // ── School Dashboard ──
  'school-dashboard': {
    title: 'School Dashboard',
    sections: [
      {
        heading: 'Student Oversight',
        bullets: [
          'Student list shows all enrolled students with engagement status and last activity.',
          'Pathway progress tracks which focus areas students are working on.',
          'Flag system highlights students who may need additional support.',
        ],
      },
      {
        heading: 'School Analytics',
        bullets: [
          'Engagement score aggregates all student activity into a single metric.',
          'Monthly reports auto-generate and are sent to school admin and parents.',
          'Leaderboard shows top-engaged students (anonymized if configured).',
        ],
      },
    ],
  },
};

export default CHEAT_SHEETS;
